import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
// React 19 native metadata
import { ChevronRight, BookOpen, Scale, FileText, Network } from 'lucide-react';
import { useBreadcrumbSchema } from '../common/useBreadcrumbSchema';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SectionHeader = ({ icon: Icon, title, count, color }) => (
  <div className="flex items-center gap-3 mb-6">
    <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center`}>
      <Icon className="w-5 h-5 text-white" />
    </div>
    <div>
      <h2 className="text-xl font-bold text-white">{title}</h2>
      <span className="text-xs text-gray-500">{count} pages</span>
    </div>
  </div>
);

const CategoryCluster = ({ category, items, linkPrefix, labelKey = 'title' }) => (
  <div className="p-5 rounded-xl bg-white/[0.02] border border-white/8" data-testid={`cluster-${category.toLowerCase().replace(/\s+/g, '-')}`}>
    <h3 className="text-sm font-semibold text-purple-400 mb-3 tracking-wide">{category}</h3>
    <div className="flex flex-wrap gap-2">
      {items.map(item => (
        <Link
          key={item.slug}
          to={`${linkPrefix}/${item.slug}`}
          className="px-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/10 text-xs text-gray-300 hover:text-purple-300 hover:border-purple-500/30 transition-all"
          data-testid={`topic-link-${item.slug}`}
        >
          {item[labelKey]}
        </Link>
      ))}
    </div>
  </div>
);

export default function TopicHub() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // SEO: BreadcrumbList JSON-LD for hub indexing
  useBreadcrumbSchema('topics-index', useMemo(() => [
    { name: 'Home', path: '/' },
    { name: 'Topics', path: '/topics' },
  ], []));

  useEffect(() => {
    fetch(`${API_URL}/api/topics`)
      .then(r => r.json())
      .then(d => setData(d))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const totalPages = data ? data.glossary.total + data.comparisons.total + data.blog.total : 0;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "iSupplyAI Content Hub — AI Marketing Topics, Comparisons & Glossary",
    "description": "Explore iSupplyAI's complete content network: blog posts, competitor comparisons, and AI marketing glossary. Your guide to multi-agent AI marketing strategy.",
    "url": "https://isupplyai.com/topics",
    "publisher": { "@type": "Organization", "name": "iSupplyAI" },
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="topic-hub-page">
      <>
        <title>AI Marketing Topics — Trends & Analysis | iSupplyAI</title>
        <meta name="description" content="Explore iSupplyAI's complete content network. Browse AI marketing blog posts, competitor comparisons, and glossary terms — all interconnected for maximum learning." />
        <link rel="canonical" href="https://isupplyai.com/topics" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="iSupplyAI" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:title" content="AI Marketing Content Hub — 129+ Interconnected Pages" />
        <meta property="og:description" content="Explore iSupplyAI's complete content network: blog posts, competitor comparisons, and AI marketing glossary." />
        <meta property="og:url" content="https://isupplyai.com/topics" />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@isupplyai" />
        <meta name="twitter:creator" content="@isupplyai" />
        <meta name="twitter:title" content="AI Marketing Content Hub — 129+ Interconnected Pages" />
        <meta name="twitter:description" content="Blog posts, competitor comparisons, and AI marketing glossary — all interconnected." />
        <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </>

      {/* Breadcrumbs */}
      <div className="max-w-6xl mx-auto px-4 pt-8">
        <nav className="flex items-center gap-2 text-sm text-gray-500" data-testid="topic-hub-breadcrumbs">
          <Link to="/" className="hover:text-gray-300 transition-colors">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-gray-300">Topics</span>
        </nav>
      </div>

      {/* Header */}
      <header className="max-w-6xl mx-auto px-4 pt-8 pb-12">
        <div className="flex items-center gap-3 mb-4">
          <Network className="w-8 h-8 text-purple-400" />
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold" data-testid="topic-hub-title">
            AI Marketing Topics Hub — <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-cyan-400">Trends, Guides & Analysis</span>
          </h1>
        </div>
        <p className="text-gray-400 text-lg max-w-3xl" data-testid="topic-hub-subtitle">
          {totalPages > 0 ? `${totalPages} interconnected pages` : 'Loading...'} covering AI marketing strategy, competitive intelligence, and multi-agent AI. Every page links to related content — explore the full network.
        </p>
      </header>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : data && (
        <div className="max-w-6xl mx-auto px-4 pb-16 space-y-16">

          {/* Blog Posts */}
          <section data-testid="topic-hub-blog">
            <SectionHeader icon={FileText} title="Blog Posts" count={data.blog.total} color="bg-purple-600" />
            <div className="grid md:grid-cols-2 gap-4">
              {data.blog.categories.map(cat => (
                <CategoryCluster
                  key={cat}
                  category={cat}
                  items={data.blog.by_category[cat]}
                  linkPrefix="/blog"
                  labelKey="title"
                />
              ))}
            </div>
            <div className="mt-4">
              <Link to="/blog" className="text-sm text-purple-400 hover:text-purple-300 transition-colors" data-testid="topic-hub-blog-link">
                View all blog posts →
              </Link>
            </div>
          </section>

          {/* Comparisons */}
          <section data-testid="topic-hub-comparisons">
            <SectionHeader icon={Scale} title="Competitor Comparisons" count={data.comparisons.total} color="bg-cyan-600" />
            <div className="grid md:grid-cols-2 gap-4">
              {data.comparisons.categories.map(cat => (
                <CategoryCluster
                  key={cat}
                  category={cat}
                  items={data.comparisons.by_category[cat].map(c => ({ slug: c.slug, title: `iSupplyAI vs ${c.competitor_name}` }))}
                  linkPrefix="/compare"
                  labelKey="title"
                />
              ))}
            </div>
            <div className="mt-4">
              <Link to="/compare" className="text-sm text-purple-400 hover:text-purple-300 transition-colors" data-testid="topic-hub-compare-link">
                View all comparisons →
              </Link>
            </div>
          </section>

          {/* Glossary */}
          <section data-testid="topic-hub-glossary">
            <SectionHeader icon={BookOpen} title="AI Marketing Glossary" count={data.glossary.total} color="bg-emerald-600" />
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.glossary.categories.map(cat => (
                <CategoryCluster
                  key={cat}
                  category={cat}
                  items={data.glossary.by_category[cat]}
                  linkPrefix="/glossary"
                  labelKey="term"
                />
              ))}
            </div>
            <div className="mt-4">
              <Link to="/glossary" className="text-sm text-purple-400 hover:text-purple-300 transition-colors" data-testid="topic-hub-glossary-link">
                View full glossary →
              </Link>
            </div>
          </section>

          {/* Stats Bar */}
          <section className="p-8 rounded-2xl bg-gradient-to-br from-purple-900/20 to-cyan-900/10 border border-purple-500/20" data-testid="topic-hub-stats">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div>
                <div className="text-2xl font-bold text-white">{data.blog.total}</div>
                <div className="text-xs text-gray-400 mt-1">Blog Posts</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{data.comparisons.total}</div>
                <div className="text-xs text-gray-400 mt-1">Comparisons</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{data.glossary.total}</div>
                <div className="text-xs text-gray-400 mt-1">Glossary Terms</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{totalPages}</div>
                <div className="text-xs text-gray-400 mt-1">Total Pages</div>
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
